import { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { ShieldCheck, EnvelopeSimple } from "@phosphor-icons/react";

/**
 * Two-step PIN change with email confirmation.
 * Step 1: user picks target role + new PIN → a 6-digit code is emailed to letantra972@gmail.com
 * Step 2: user enters the code → PIN is updated
 */
export default function PinChangeDialog({ open, onOpenChange, initialTarget = "admin" }) {
  const [step, setStep] = useState(1);
  const [target, setTarget] = useState(initialTarget);
  const [newPin, setNewPin] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep(1);
    setTarget(initialTarget);
    setNewPin("");
    setCode("");
    setBusy(false);
  };

  const requestCode = async () => {
    if (!/^\d{3,8}$/.test(newPin)) {
      toast.error("Le nouveau PIN doit contenir 3 à 8 chiffres");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/pin-change/request", { target, new_pin: newPin });
      toast.success("Code envoyé à letantra972@gmail.com");
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async () => {
    if (!/^\d{4,8}$/.test(code)) {
      toast.error("Code invalide");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/pin-change/confirm", { target, code });
      toast.success(`PIN ${target === "admin" ? "Patron" : "Manageuse"} mis à jour`);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Code invalide ou expiré");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="bg-[#121217] border-[#27272a] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <ShieldCheck size={22} color="#fbbf24" weight="fill" />
            Changer le code PIN
          </DialogTitle>
          <DialogDescription className="text-[#a1a1aa]">
            Un code de confirmation est envoyé à <b>letantra972@gmail.com</b> pour valider le changement.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label className="label-xs">Compte à modifier</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="pin-change-target" className="bg-[#09090b] border-[#27272a]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121217] border-[#27272a] text-white">
                  <SelectItem value="admin">Patron</SelectItem>
                  <SelectItem value="manager">Manageuse</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-xs">Nouveau code PIN (3 à 8 chiffres)</Label>
              <Input
                data-testid="pin-change-new-pin"
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="•••"
                className="bg-[#09090b] border-[#27272a] text-white font-mono tracking-widest text-lg text-center h-12"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-md border border-[#27272a] bg-[#09090b]">
              <EnvelopeSimple size={20} color="#fbbf24" className="mt-0.5" />
              <div className="text-sm text-[#a1a1aa]">
                Un code à 6 chiffres a été envoyé à <b className="text-white">letantra972@gmail.com</b>.
                Saisissez-le ci-dessous pour valider le nouveau PIN.
              </div>
            </div>
            <div>
              <Label className="label-xs">Code de confirmation</Label>
              <Input
                data-testid="pin-change-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className="bg-[#09090b] border-[#27272a] text-white font-mono tracking-[0.5em] text-xl text-center h-14"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 2 && (
            <Button
              variant="outline"
              onClick={() => setStep(1)}
              className="border-[#27272a] text-white hover:bg-[#1a1a21]"
              data-testid="pin-change-back"
            >
              Retour
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onOpenChange(false);
            }}
            className="border-[#27272a] text-white hover:bg-[#1a1a21]"
            data-testid="pin-change-cancel"
          >
            Annuler
          </Button>
          {step === 1 && (
            <Button
              onClick={requestCode}
              disabled={busy || !newPin}
              className="bg-[#fbbf24] hover:bg-[#f59e0b] text-black"
              data-testid="pin-change-send-code"
            >
              {busy ? "Envoi..." : "Envoyer le code"}
            </Button>
          )}
          {step === 2 && (
            <Button
              onClick={confirmCode}
              disabled={busy || code.length < 4}
              className="bg-[#fbbf24] hover:bg-[#f59e0b] text-black"
              data-testid="pin-change-confirm"
            >
              {busy ? "Vérification..." : "Valider le PIN"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
